













































































































import { Component, Prop, Vue } from 'vue-property-decorator';

import { vatModelsSizes } from './models/vat-models-sizes';
import { Vat, VatFormula, VatState } from '@/api';
import Toast from '@/components/display/toast.vue';
import { VatModals } from './models/vat-modals';
import { VatService } from './service/vat-service';
import { numberInputValid } from '@/common/Validators';
import CustomFormulaConfiguration from '@/components/form pickers/CustomFormulaConfiguration.vue';
import { Constants } from '@/common/Constants';
import { FormulaInput } from '@/modules/formulas/models/formula-input';
import { FormulasService } from '@/modules/formulas/formulas-service';
import SaveCancelButtons from '@/components/elements/buttons/save-cancel-buttons.vue';
import TooltipButton from '@/components/elements/buttons/tooltip-button.vue';
import { getOtherActionText } from '@/common/HelpTextUtils';

@Component({
	components: {
		SaveCancelButtons,
		CustomFormulaConfiguration,
		TooltipButton,
	},
	methods: { getOtherActionText },
})
export default class ProvisionVatModal extends Vue {
	@Prop({ required: true, type: Object })
	public vat!: Vat;

	@Prop({ required: true, type: Function })
	private onProvision!: (vat: Vat) => Promise<void>;

	@Prop({ required: true, type: Boolean })
	public provisionedPreviously!: boolean;

	public get sizeStringValid(): boolean {
		return this.sizeString !== null && this.sizeString !== '';
	}

	public get vatOffsetValid(): boolean {
		return numberInputValid({
			field: this.vatOffset,
			min: -1000,
			max: 1000,
		});
	}

	public get emptyDistanceValid(): boolean {
		return numberInputValid({
			field: this.emptyDistance,
			min: 0,
			max: 8000,
		});
	}

	public get stirrerThreshValid(): boolean {
		if (this.stirrerThresh === null) {
			return true;
		}
		return numberInputValid({
			field: this.stirrerThresh,
			min: 0,
			max: 1000,
		});
	}

	public get disableDefaultDistanceButton(): boolean {
		if (!this.formula || !this.sizeString) {
			return true;
		}
		const emptyDistance = this.calculateEmptyDistance(
			this.formula,
			this.sizeString,
			this.vatOffset,
		);
		return emptyDistance === this.emptyDistance;
	}

	public get vatFormulas() {
		return [
			{ value: VatFormula.FonterraVatII, text: 'Fonterra Vat II' },
			{ value: VatFormula.Longveld, text: 'Longveld' },
			{ value: VatFormula.Butterfly, text: 'Butterfly' },
			{ value: VatFormula.Spherical, text: 'Spherical' },
			{ value: VatFormula.Crown, text: 'Crown' },
			{ value: VatFormula.Custom, text: 'Custom' },
		];
	}

	public get vatSizes() {
		if (this.formula !== null && this.formula !== VatFormula.Custom) {
			return vatModelsSizes[this.formula];
		}
	}

	public get formulaOptions() {
		return this.formulasService.formulaOptions;
	}

	public get canSubmit(): boolean {
		return (
			this.vatOffsetValid &&
			this.emptyDistanceValid &&
			this.stirrerThreshValid &&
			this.sizeString !== null &&
			this.sizeString !== ''
		);
	}

	public get showCustomFormulaConfig(): boolean {
		return this.formula === VatFormula.Custom;
	}

	public formula: VatFormula | null = this.vat.formula ?? null;
	public sizeString: string | null = this.vat.sizeString ?? null;
	public vatOffset: number = this.vat.vatOffset ?? 250;
	public emptyDistance: number = this.vat.emptyDistance ?? 0;
	public stirrerThresh: number | null = this.vat.stirrerThresh ?? null;
	public modalId: string = VatModals.PROVISION;
	public customFormulaInputs: FormulaInput[] = [];
	public customFormulaId: number | null = this.vat.formulaId ?? null;
	public saving: boolean = false;
	public label: string = this.vat.label ?? '';

	private formulasService = new FormulasService(Constants.ENTITY_TYPE_MILK_VAT);

	private state: VatState = this.vat.state ?? VatState.Supply;
	private toast: Toast = new Toast();

	public async created(): Promise<void> {
		await this.formulasService.initalise();
		if (this.vat.formulaId) {
			this.setFormulaInputs(this.vat.formulaId);
			if (this.vat.formulaParameters) {
				const parameters: Record<string, any> = this.vat.formulaParameters;
				this.customFormulaInputs.forEach((input: FormulaInput) => {
					input.value = parameters[input.name];
					if (input.value) {
						input.valid = true;
					}
				});
			}
		}
	}

	public onFormulaChanged(formula: VatFormula): void {
		this.formula = formula;
		if (this.vatSizes) {
			this.sizeString = this.vatSizes[0].value;
		} else {
			this.sizeString = null;
		}
		if (formula === VatFormula.Custom) {
			this.sizeString = this.vat.sizeString ?? null;
		}
		this.setDefaultEmptyDistance('input');
	}

	public onCustomFormulaInputsChanged(inputs: FormulaInput[]): void {
		this.customFormulaInputs = inputs;
	}

	public onCustomFormulaChanged(formulaId: number): void {
		this.customFormulaId = formulaId;
		this.setFormulaInputs(formulaId);
	}

	public onVatSizeChanged(_vatSize: string): void {
		this.setDefaultEmptyDistance('input');
	}

	public onStirrerThreshChanged(stirrerThresh: string | number): void {
		if (stirrerThresh === '') {
			this.stirrerThresh = null;
		}
	}

	public async provisionVat(): Promise<void> {
		this.saving = true;
		if (this.formula === null) {
			this.toast.makeToast('You must provide the vat formula', 'Missing properties', 'warning');
			this.saving = false;
			return;
		}

		if (this.formula === VatFormula.Custom) {
			this.customFormulaInputs.forEach((input: FormulaInput) => (input.valid = true));
			const invalidInputs = this.customFormulaInputs.filter((input: FormulaInput) => input.value == null || input.value.toString().length == 0);
			if (invalidInputs.length > 0) {
				invalidInputs.forEach((input: FormulaInput) => (input.valid = false));
				this.toast.makeToast(
					'You must provide a value for all custom formula inputs',
					'Missing properties',
					'warning',
				);
				this.saving = false;
				return;
			}

			const parameters: Record<string, any> = {};
			this.customFormulaInputs.forEach((input: FormulaInput) => {
				parameters[input.name] = input.value;
			});
			parameters['offset'] = this.vatOffset;
			this.vat.formulaId = this.customFormulaId;
			this.vat.formulaParameters = parameters;
		} else {
			this.vat.formulaId = null;
			this.vat.formulaParameters = null;
		}

		this.vat.sizeString = this.sizeString;
		this.vat.label = this.label;
		this.vat.vatOffset = this.vatOffset;
		this.vat.emptyDistance = this.emptyDistance;
		this.vat.state = this.state;
		this.vat.formula = this.formula;
		this.vat.stirrerThresh = this.stirrerThresh;
		await this.onProvision(this.vat);
		this.saving = false;
	}

	public setDefaultEmptyDistance(triggeredFrom: 'input' | 'manually' = 'manually'): void {
		if (!this.formula || !this.sizeString) {
			if (triggeredFrom === 'manually') {
				// Don't spam annoying toasts on when user is still selecting
				this.toast.makeToast(
					'You must select the vat size and formula first',
					'Missing properties',
					'warning',
				);
			}
			return;
		}
		const emptyDistance = this.calculateEmptyDistance(
			this.formula,
			this.sizeString,
			this.vatOffset,
		);
		if (emptyDistance === undefined) {
			return;
		}
		this.emptyDistance = emptyDistance;
	}

	public dismiss(): void {
		this.$bvModal.hide(VatModals.PROVISION);
	}

	private calculateEmptyDistance(
		vatFormula: string,
		vatSize: string,
		vatOffset: number,
	): number | undefined {
		const vatHeight: number | undefined = VatService.getVatHeight(vatFormula, vatSize);
		if (vatHeight === undefined) {
			return undefined;
		}
		return vatHeight + vatOffset;
	}

	private setFormulaInputs(formulaId: number): void {
		this.customFormulaInputs = (this.formulasService.getFormulaInputs(formulaId) ?? []).filter(
			(input: FormulaInput) => input.name !== 'offset',
		); // We already record offset by default, don't need to show it twice
	}
}
